import avatar1 from "@/assets/avatars/avatar-1.png";
import avatar2 from "@/assets/avatars/avatar-2.png";
import avatar3 from "@/assets/avatars/avatar-3.png";
import avatar4 from "@/assets/avatars/avatar-4.png";
import avatar5 from "@/assets/avatars/avatar-5.png";
import avatar6 from "@/assets/avatars/avatar-6.png";
import avatar7 from "@/assets/avatars/avatar-7.png";
import avatar8 from "@/assets/avatars/avatar-8.png";
import novaTutor from "@/assets/avatars/nova-tutor.png";
import parentAvatarImg from "@/assets/avatars/parent-avatar.png";

export type CharacterCustomization = {
  avatarId: string;
  skinTone?: string;
  hairStyle?: string;
  hairColor?: string;
  eyeColor?: string;
  faceShape?: string;
};

export const DEFAULT_CUSTOMIZATION: CharacterCustomization = {
  avatarId: "avatar-1",
};

export const AVATAR_OPTIONS: { id: string; name: string; src: string }[] = [
  { id: "avatar-1", name: "Oliver", src: avatar1 },
  { id: "avatar-2", name: "Amara", src: avatar2 },
  { id: "avatar-3", name: "Jayden", src: avatar3 },
  { id: "avatar-4", name: "Rosie", src: avatar4 },
  { id: "avatar-5", name: "Kai", src: avatar5 },
  { id: "avatar-6", name: "Priya", src: avatar6 },
  { id: "avatar-7", name: "Finn", src: avatar7 },
  { id: "avatar-8", name: "Luna", src: avatar8 },
];

const avatarMap: Record<string, string> = {};
AVATAR_OPTIONS.forEach((a) => {
  avatarMap[a.id] = a.src;
});

export const SKIN_TONES: Record<string, { fill: string; shadow: string; label: string }> = {};
export const HAIR_COLORS: Record<string, { fill: string; shadow: string; label: string }> = {};
export const HAIR_STYLES: Record<string, { label: string }> = {};
export const EYE_COLORS: Record<string, { fill: string; label: string }> = {};
export const FACE_SHAPES: Record<string, { label: string }> = {};

type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

const sizeMap: Record<AvatarSize, number> = {
  xs: 24,
  sm: 32,
  md: 48,
  lg: 64,
  xl: 80,
};

interface AvatarImageProps {
  customization?: CharacterCustomization | null;
  size?: AvatarSize;
  className?: string;
  showFrame?: boolean;
  rarity?: string;
}

const rarityFrameColors: Record<string, string> = {
  common: "#94a3b8",
  uncommon: "#4ade80",
  rare: "#60a5fa",
  epic: "#a855f7",
  legendary: "#f59e0b",
};

function resolveAvatarSrc(customization?: CharacterCustomization | null): string {
  if (!customization) return avatar1;
  if (customization.avatarId && avatarMap[customization.avatarId]) {
    return avatarMap[customization.avatarId];
  }
  return avatar1;
}

export default function AvatarImage({
  customization,
  size = "md",
  className = "",
  showFrame = true,
  rarity,
}: AvatarImageProps) {
  const dim = sizeMap[size];
  const src = resolveAvatarSrc(customization);
  const frameColor = rarity ? rarityFrameColors[rarity] || "#94a3b8" : showFrame ? "#c7d2fe" : "transparent";

  return (
    <div
      className={`relative flex-shrink-0 ${className}`}
      style={{ width: dim, height: dim }}
    >
      <img
        src={src}
        alt="Avatar"
        className="w-full h-full rounded-full object-cover"
        style={{
          border: showFrame || rarity ? `${dim > 40 ? 2.5 : 2}px solid ${frameColor}` : "none",
        }}
        draggable={false}
      />
    </div>
  );
}

export function NovaAvatar({ size = "md", className = "" }: { size?: AvatarSize; className?: string }) {
  const dim = sizeMap[size];
  return (
    <div
      className={`relative flex-shrink-0 ${className}`}
      style={{ width: dim, height: dim }}
    >
      <img
        src={novaTutor}
        alt="Nova"
        className="w-full h-full rounded-full object-cover"
        style={{ border: `2px solid #818cf8` }}
        draggable={false}
      />
    </div>
  );
}

export function ParentAvatar({ size = "md", className = "" }: { size?: AvatarSize; className?: string }) {
  const dim = sizeMap[size];
  return (
    <div
      className={`relative flex-shrink-0 ${className}`}
      style={{ width: dim, height: dim }}
    >
      <img
        src={parentAvatarImg}
        alt="Parent"
        className="w-full h-full rounded-full object-cover"
        style={{ border: `2px solid #818cf8` }}
        draggable={false}
      />
    </div>
  );
}

let itemIconCounter = 0;

const rarityGradients: Record<string, [string, string, string]> = {
  common: ["#e2e8f0", "#cbd5e1", "#94a3b8"],
  uncommon: ["#bbf7d0", "#86efac", "#4ade80"],
  rare: ["#bfdbfe", "#93c5fd", "#60a5fa"],
  epic: ["#e9d5ff", "#c084fc", "#a855f7"],
  legendary: ["#fef08a", "#fbbf24", "#f59e0b"],
};

export function ItemIcon({ iconUrl, category, rarity, size = "md", className = "" }: {
  iconUrl: string;
  category?: string;
  rarity?: string;
  size?: AvatarSize;
  className?: string;
}) {
  const dim = sizeMap[size];
  const gradient = rarityGradients[rarity || "common"];
  const frameColor = rarityFrameColors[rarity || "common"];
  const uid = `item-bg-${++itemIconCounter}`;

  return (
    <div
      className={`relative flex-shrink-0 flex items-center justify-center ${className}`}
      style={{ width: dim, height: dim }}
    >
      <svg width={dim} height={dim} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id={uid} cx="30%" cy="30%" r="70%">
            <stop offset="0%" stopColor={gradient[0]} />
            <stop offset="60%" stopColor={gradient[1]} />
            <stop offset="100%" stopColor={gradient[2]} />
          </radialGradient>
        </defs>
        <circle cx="50" cy="50" r="46" fill={`url(#${uid})`} />
        <circle cx="50" cy="50" r="46" fill="none" stroke={frameColor} strokeWidth="2.5" opacity="0.6" />
        <text x="50" y="54" textAnchor="middle" dominantBaseline="central" fontSize="40" style={{ userSelect: "none" }}>{iconUrl}</text>
      </svg>
    </div>
  );
}
